<template>
  <v-container
    fluid
    class="ac2-page">
    <v-row>
      <v-col
        cols="12"
        :md="selectItem ? '8' : '12'">
        <v-card
          class="rounded-lg table-card">
          <v-data-table
            :headers="header"
            :items="items"
            :options.sync="options"
            :items-per-page="options.itemsPerPage"
            :server-items-length="total"
            :loading="loading"
            hide-default-footer
            class="w-full"
            @update:options="onOptionsUpdate"
          >
            <template v-slot:top>
              <div class="d-flex align-center flex-wrap px-4 pt-4 pb-2">
                <h6 class="text-h6 mb-0">
                  AC2 Access Token Logs
                </h6>
                <v-spacer />
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="mr-2"
                  :loading="generating"
                  @click="generateToken()">
                  <v-icon
                    size="16px"
                    class="mr-1">
                    mdi-key-plus
                  </v-icon>
                  Generate Token
                </v-btn>
                <v-btn
                  icon
                  @click="getData()">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>
              <v-divider />
            </template>

            <template #[`item.createdAt`]="{ item }">
              {{ item.createdAt | dateTimeFormat() }}
            </template>
            <template #[`item.expireAt`]="{ item }">
              {{ item.expireAt ? $options.filters.dateTimeFormat(item.expireAt) : '-' }}
            </template>
            <template #[`item.status`]="{ item }">
              <div
                class="text-bold status"
                :style="getStatusStyle(item.status)">
                {{ item.status.toUpperCase() }}
              </div>
            </template>
            <template #[`item.option`]="{ item }">
              <v-btn
                v-if="!selectItem || selectItem.id !== item.id"
                link
                icon
                x-small
                @click="showItemDetail(item)">
                <v-icon>
                  mdi-magnify
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-divider />
          <div class="d-flex align-center flex-wrap px-4 py-3">
            <span class="text-caption grey--text">
              Total {{ total }} items
            </span>
            <v-spacer />
            <v-pagination
              v-model="options.page"
              :length="pageCount"
              :total-visible="5"
              color="secondary"
              @input="getData()" />
          </div>
        </v-card>
      </v-col>
      <v-col
        v-if="selectItem"
        cols="12"
        md="4">
        <v-card class="rounded-lg detail-card pa-4">
          <p class="mb-1 text-bold">
            Status
            <span
              class="status"
              :style="getStatusStyle(selectItem.status)">
              {{ selectItem.status.toUpperCase() }}
            </span>
          </p>
          <p class="mb-1">
            <b>Code </b>{{ selectItem.code }}
          </p>
          <p class="mb-1">
            <b>Request ID </b>{{ selectItem.requestId }}
          </p>
          <p class="mb-1">
            <b>Expire At </b>{{ selectItem.expireAt ? $options.filters.dateTimeFormat(selectItem.expireAt) : '-' }}
          </p>
          <p class="mb-1">
            <b>Timestamp </b>{{ selectItem.timestamp }}
          </p>
          <p class="mb-1">
            <b>Created At </b>{{ selectItem.createdAt | dateTimeFormat() }}
          </p>

          <p class="mt-4 mb-1 font-weight-medium">
            Data
          </p>
          <div class="json-box">
            <json-viewer
              :value="selectItem"
              :expand-depth="5"
              copyable
              sort />
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Ac2Provider from '@/resources/Ac2Provider'

const Ac2Service = new Ac2Provider()

export default {
  data () {
    return {
      loading: false,
      generating: false,
      selectItem: null,
      showDetail: false,
      total: 0,
      sortSnapshot: null,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      header: [
        {
          text: 'Created At',
          value: 'createdAt',
          sortable: false
        },
                {
          text: 'Expire At',
          value: 'expireAt'
        },
        {
          text: 'Code',
          value: 'code',
          sortable: false
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false
        },
        {
          text: 'Request ID',
          value: 'requestId',
          sortable: false
        },
        {
          text: 'Options',
          value: 'option',
          align: 'center',
          sortable: false
        }
      ],
      items: []
    }
  },
  computed: {
    pageCount () {
      return Math.ceil(this.total / this.options.itemsPerPage) || 1
    }
  },
  created () {
    this.getData(true)
  },
  methods: {
    onOptionsUpdate () {
      // Paging is handled by the separate v-pagination, so only refetch
      // when the sort changes. Skip the initial mount emit (created() loads).
      const sortKey = `${this.options.sortBy.join(',')}|${this.options.sortDesc.join(',')}`
      if (this.sortSnapshot === null) {
        this.sortSnapshot = sortKey
        return
      }
      if (sortKey !== this.sortSnapshot) {
        this.sortSnapshot = sortKey
        this.getData(true)
      }
    },
    showItemDetail (item) {
      this.selectItem = item
      this.showDetail = true
    },
    getStatusStyle (status) {
      switch (status) {
        case 'active':
          return 'color: #3b9c28; background-color: #eef6e9;'
        case 'expired':
          return 'color: #e93e4c; background-color: #ffecec;'
        case 'deleted':
          return 'color: #e93e4c; background-color: #ffecec;'
        case 'inactive':
          return 'color: #d4d4d4; background-color: #f8f8f8;'
        default:
          return 'color: #18171d; background-color: #f8f8f8;'
      }
    },
    async getData (clear = false) {
      if (clear) {
        this.options.page = 1
      }
      try {
        this.loading = true
        const { data: { results, totalDocs } } = await Ac2Service.getAccessTokens(
          {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            sortBy: this.options.sortBy[0],
            sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc'
          }
        )

        this.items = results.map((re) => {
          let { status } = re
          if (re.expireAt && status === 'active' && new Date() - new Date(re.expireAt) > 0) {
            status = 'expired'
          }

          return {
            ...re,
            status
          }
        })
        this.total = totalDocs
        if (clear && results.length !== 0) {
          this.selectItem = this.items[0]
        }
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async generateToken () {
      try {
        this.generating = true
        const data = await Ac2Service.generateAccessToken()
        if (data) {
          this.$store.dispatch('Components/setSnackbar', {
            value: true,
            message: 'Generate token success',
            type: 'success'
          })
        }
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.generating = false
        await this.getData(true)
      }
    }
  }
}
</script>
<style scoped>
.table-card,
.detail-card {
  height: calc(100vh - 96px);
  overflow-y: auto;
}
.json-box {
  border: thin solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}
.theme--dark .json-box {
  border-color: rgba(255, 255, 255, 0.12);
}
.status {
  padding: 4px;
  border-radius: 6px;
  width: 80px;
  text-align: center;
  display: inline-block;
}
</style>
